import ServiceTemplate from './service-page-template/service-page-template.jsx';
import { imagesMetadata } from '../../imagesMetadata.js';

const heroImageSrc = '/static/images/plastering-painting/st-kilda-interior-hallway-painting-01.webp';
const heroMeta = imagesMetadata[heroImageSrc];

const excludedImages = [
  heroImageSrc,
  // Add any other image paths to exclude here...
];

const plasteringPaintingGalleryImages = Object.entries(imagesMetadata)
  .filter(([src]) => src.startsWith('/static/images/plastering-painting/') && !excludedImages.includes(src))
  .map(([src, meta], index) => ({
    id: index + 1,
    src,
    alt: meta.alt,
    location: meta.location
  }));

export default function PlasteringPainting() {
  const servicesData = {
    title: "Plastering & Painting Services",
    subtitle: "Professional wall patching, plastering, interior painting, and exterior painting including decks & fences.",
    metaTitle: "Plastering & Painting Melbourne | Wall Repairs & Patching",
    metaDesc: "Melbourne plaster repairs, wall patching and interior or exterior painting. Refresh walls, ceilings, decks and fences with a neat local handyman service.",
    canonicalPath: "/plastering-painting",

    solutionsTitle: "Our Patching & Painting Solutions",
    solutionsIntro: "From precise interior work to durable exterior painting, we deliver smooth, long-lasting results on all surfaces.",

    solutionsList: [
      { 
        emoji: "🕳️", 
        heading: "Wall Patching & Hole Repair", 
        text: "Professional repair of cracks, dents, nail holes, and larger damage for invisible repairs." 
      },
      { 
        emoji: "🧱", 
        heading: "Plastering & Plasterboard Replacement", 
        text: "New plastering, damaged plasterboard replacement, and full wall restoration." 
      },
      { 
        emoji: "🎨", 
        heading: "Interior Painting", 
        text: "Walls, ceilings, trims, and feature walls with premium paints and sharp, clean lines." 
      },
      { 
        emoji: "🏠", 
        heading: "Exterior, Deck & Fence Painting", 
        text: "House exteriors, timber decks, fences, and outdoor structures with weather-resistant coatings." 
      }
    ],

    serviceImage: {
      src: heroImageSrc,
      alt: heroMeta?.alt || 'Expert interior painter in St Kilda showcasing a finished hallway and console entryway display by Handyman In Melbourne'
    },

    extendedDetails: [
      {
        title: "Expert Exterior & Timber Protection",
        body: "We specialise in deck and fence painting using high-quality exterior paints and stains that protect against Melbourne’s weather — extending the life of your timber while keeping it looking great."
      },
      {
        title: "Flawless Interior Finishes",
        body: "Meticulous surface preparation, fine feathering of patches, and professional cutting-in techniques ensure smooth, uniform walls and ceilings with no visible repairs."
      },
      {
        title: "Premium Materials & Colour Matching",
        body: "We use durable, low-VOC paints and can accurately match existing colours or help you choose new ones suited to your home and lifestyle."
      },
      {
        title: "Clean, Professional Work Sites",
        body: "Full protection of your property, effective dust control, and thorough cleanup after every job — whether it’s an interior room or a full deck and fence repaint."
      }
    ],

    galleryImages: plasteringPaintingGalleryImages,

    faqs: [
      {
        question: 'Do you handle small patch repairs or full rooms?',
        answer: 'Both. We specialise in invisible patch repairs as well as complete painting projects.'
      },
      {
        question: 'Can you colour match existing paint?',
        answer: 'Yes, we perform professional colour matching. However, there is a chance it won’t match 100% because lighting conditions, the age of the existing paint, and natural fading over time can affect the final result. We always do our best to get as close as possible and will discuss expectations with you before starting.'
      },
      {
        question: 'How long does plaster need to dry?',
        answer: 'Usually 24–48 hours depending on conditions before painting.'
      },
      {
        question: 'Do you repair water damage and cracks?',
        answer: 'Yes. We fix holes, cracks, cornices, and water-damaged areas.'
      },
      {
        question: 'Do you protect furniture and floors?',
        answer: 'Always. We use drop sheets, masking, and careful preparation.'
      },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": servicesData.title,
    "serviceType": servicesData.title,
    "description": servicesData.metaDesc,
    "url": `https://handymaninmelbourne.au${servicesData.canonicalPath}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Handyman In Melbourne",
      "url": "https://handymaninmelbourne.au/"
    },
      "areaServed": [
            "City of Melbourne VIC",
            "City of Port Phillip VIC",
            "City of Glen Eira VIC",
            "City of Stonnington VIC",
            "City of Boroondara VIC",
            "City of Whitehorse VIC",
            "City of Manningham VIC",
            "City of Monash VIC",
            "City of Kingston VIC",
            "City of Yarra VIC",
            "City of Moonee Valley VIC",
            "City of Darebin VIC",
            "City of Greater Dandenong VIC"
        ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Plastering & Painting Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Wall Patching & Hole Repair"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Plastering & Plasterboard Replacement"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Interior Painting"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Exterior, Deck & Fence Painting"
            }
          }
        ]
      }
  };

  return(
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <ServiceTemplate {...servicesData} />
    </>
  );
}
